/**
 * FloorScore — вклад этажа в оценку кабинета.
 *
 * Стоит справа от заголовка и объяснения и отвечает на вопрос, который до
 * сих пор оставался без ответа: «а это вообще на балл влияет?». Раньше связь
 * между разделом и слагаемым существовала только в разборе оценки — свёрнутом
 * по умолчанию, — и сверять их приходилось по названиям.
 *
 * Заголовок этажа уже сказал, о чём раздел, поэтому подпись слагаемого здесь
 * не повторяется. Исключение — этаж с двумя слагаемыми (инциденты: критичные
 * и обычные): без подписей два числа рядом неразличимы.
 *
 * Причины вычета тут нет намеренно. Она объясняет ПОТЕРЮ балла, а весь
 * следующий за заголовком этаж — это и есть развёрнутая причина; короткая
 * формулировка перед ним отобрала бы у него ответ.
 *
 * @format
 */

import {Box} from '@mui/material';
import {styled} from '@mui/material/styles';

import {Row} from 'ui/components/layout/Row';
import {Stack} from 'ui/components/layout/Stack';
import {spacingPx} from 'ui/components/layout/spacing';
import {text} from 'ui/theme/typography';
import {surfaceVar} from 'ui/theme/surfaceContext';
import {RadiusM} from 'ui/tokens/ts/radii';

import {POINTS, TABULAR, plural} from '../helpers';
import {scorePartLabel, scorePartTone} from '../content';
import type {ScorePart} from '../types';
import {MeterBar} from './MeterBar';

export interface FloorScoreProps {
  /** Слагаемые балла, отвечающие за этот этаж. Пусто — блока нет. */
  parts: ScorePart[];
}

/**
 * Ширина блока.
 *
 * Хватает на «12 из 15 баллов» в одну строку и на подпись самого длинного
 * слагаемого в две. Шире — начинает спорить с объяснением этажа за роль
 * главного текста, уже — рвёт число и единицу по разным строкам.
 */
const WIDTH = 210;

const Panel = styled(Box)({
  flex: 'none',
  width: WIDTH,
  padding: spacingPx('m'),
  borderRadius: RadiusM,
  border: `1px solid ${surfaceVar('surface2')}`,
  // Без заливки: этаж ниже почти всегда начинается карточкой, и вторая
  // плашка рядом с заголовком превратила бы шапку в ещё один блок.
  // Контур очерчивает ровно столько, сколько нужно, чтобы числа не
  // читались как продолжение заголовка.
  '@media (max-width: 1024px)': {
    width: '100%',
  },
});

/** «Вклад в оценку» — над числами, потому что иначе они ничьи. */
const Caption = styled(Box)({
  ...text('label.s'),
  color: surfaceVar('onSurfaceMuted'),
});

const Got = styled(Box, {
  shouldForwardProp: prop => !String(prop).startsWith('$'),
})<{$tone: 'good' | 'alert' | 'bad'}>(({theme, $tone}) => ({
  ...text('heading.m'),
  ...TABULAR,
  color: theme.palette[$tone].main,
}));

/** Потолок слагаемого — тише набранного: это мера, а не результат. */
const Max = styled(Box)({
  ...text('body.m'),
  ...TABULAR,
  color: surfaceVar('onSurfaceLight'),
});

const PartLabel = styled(Box)({
  ...text('body.s'),
  color: surfaceVar('onSurfaceMuted'),
});

export function FloorScore({parts}: FloorScoreProps) {
  if (!parts.length) return null;

  // Подписи нужны только там, где слагаемых больше одного: на остальных
  // этажах их назвал заголовок.
  const named = parts.length > 1;

  return (
    <Panel>
      <Stack gap='s'>
        <Caption>Вклад в оценку</Caption>

        {parts.map(part => (
          <Stack key={part.key} gap='xxs'>
            {named && <PartLabel>{scorePartLabel(part.key)}</PartLabel>}
            <Row gap='xs' alignY='baseline'>
              <Got $tone={scorePartTone(part.got, part.max)}>{part.got}</Got>
              <Max>
                из {part.max} {plural(part.max, POINTS)}
              </Max>
            </Row>
            <MeterBar
              value={part.max > 0 ? part.got / part.max : 0}
              tone={scorePartTone(part.got, part.max)}
              height={6}
            />
          </Stack>
        ))}
      </Stack>
    </Panel>
  );
}
